/**
 * SearchableSelect — dropdown com busca por texto.
 * Substitui <Select> shadcn nos filtros e campos com listas médias/grandes.
 *
 * Props:
 * - value: string (id selecionado)
 * - onValueChange: (id: string) => void
 * - options: [{ value, label, hint? }]  (label é o texto principal; hint texto secundário opcional)
 * - placeholder: string (quando nada selecionado)
 * - searchPlaceholder: string (dentro do popover)
 * - emptyText: string
 * - allowClear: bool (mostra opção "— (Todos) —" que envia value="")
 * - className: string extra pro Button
 * - disabled: bool
 * - testid: string
 */
import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export function SearchableSelect({
  value,
  onValueChange,
  options = [],
  placeholder = "Selecionar...",
  searchPlaceholder = "Pesquisar...",
  emptyText = "Nenhum resultado.",
  allowClear = false,
  disabled = false,
  className = "",
  testid = "searchable-select",
}) {
  const [open, setOpen] = useState(false);
  const sorted = useMemo(
    () => [...(options || [])].sort((a, b) => (a.label || "").localeCompare(b.label || "", "pt-BR")),
    [options],
  );
  const selected = sorted.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "w-full justify-between font-normal h-9 px-3 text-sm",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate text-left">
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          filter={(v, search) => {
            const opt = sorted.find((o) => o.value === v);
            if (!opt) return 0;
            const hay = `${opt.label || ""} ${opt.hint || ""}`.toLowerCase();
            return hay.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testid}-input`} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {allowClear && (
                <CommandItem
                  value=""
                  onSelect={() => {
                    onValueChange("");
                    setOpen(false);
                  }}
                  data-testid={`${testid}-clear`}
                  className="text-muted-foreground italic"
                >
                  <X className="mr-2 h-4 w-4" />— Todos —
                </CommandItem>
              )}
              {sorted.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.value}
                  onSelect={() => {
                    onValueChange(o.value);
                    setOpen(false);
                  }}
                  data-testid={`${testid}-option-${o.value}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === o.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm">{o.label}</span>
                    {o.hint && <span className="text-[10px] text-muted-foreground">{o.hint}</span>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
